/**
 * InputPanel — Slim control panel: core deal inputs only.
 * Address, map, land intelligence, quick test, and market price
 * now live in the DecisionCockpit (right panel).
 */
import { useState } from 'react';
import { ClearableNumberInput } from './ClearableNumberInput';
import { applyUnitsChange, applyParkingSpotsChange } from '../utils/parkingSpotsLink';
import { INPUT_CONFIG, clampInputValue } from '../types/dealTypes';
import { withSyncedSalePrice } from '../utils/salePriceBlend';
import { isEffectivelyZero } from '../utils/clearableNumber';

function Field({ label, value, onChange, unit, step = 1, id, min, max, readOnly = false }) {
  const config = INPUT_CONFIG[id];
  const minVal = min ?? config?.min ?? 0;
  const maxVal = max ?? config?.max;

  return (
    <div className="flex items-center gap-0.5">
      <label htmlFor={id} className="text-[9px] md:text-[8px] text-gray-500 uppercase w-12 shrink-0 leading-none">{label}</label>
      <div className="relative flex-1">
        <ClearableNumberInput
          id={id}
          value={value}
          onChange={onChange}
          step={step}
          min={minVal}
          max={maxVal}
          readOnly={readOnly}
          disabled={readOnly}
          aria-label={label}
          data-testid={`input-${id}`}
          className={`h-[36px] md:h-6 min-h-[24px] px-2 md:px-1 text-[14px] md:text-[10px] tabular-nums border-gray-200 rounded pr-8 md:pr-6 ${
            readOnly ? 'bg-slate-100 text-slate-700 cursor-default' : 'bg-gray-50'
          }`}
        />
        {unit && <span className="absolute right-1.5 md:right-1 top-1/2 -translate-y-1/2 text-[10px] md:text-[7px] text-gray-400 pointer-events-none">{unit}</span>}
      </div>
    </div>
  );
}

function UnitsField({ value, onChange, readOnly = false }) {
  const [focused, setFocused] = useState(false);
  const config = INPUT_CONFIG.numberOfUnits;

  return (
    <div className="flex items-center gap-0.5">
      <label htmlFor="numberOfUnits" className="text-[9px] md:text-[8px] text-gray-500 uppercase w-12 shrink-0 leading-none">Units</label>
      <div
        className="relative flex-1"
        onFocusCapture={() => setFocused(true)}
        onBlurCapture={() => setFocused(false)}
      >
        <ClearableNumberInput
          id="numberOfUnits"
          value={value}
          onChange={(v) => onChange(clampInputValue('numberOfUnits', v))}
          step={1}
          min={config.min}
          max={config.max}
          readOnly={readOnly}
          disabled={readOnly}
          aria-label="Units"
          data-testid="input-numberOfUnits"
          className={`h-[36px] md:h-6 min-h-[24px] px-2 md:px-1 text-[14px] md:text-[10px] tabular-nums border-gray-200 rounded pr-10 md:pr-8 ${
            readOnly ? 'bg-slate-100 text-slate-700 cursor-default' : 'bg-gray-50'
          }`}
        />
        {isEffectivelyZero(value) && !focused && (
          <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[6px] text-blue-500 font-bold pointer-events-none">AUTO</span>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[10px] md:text-[7px] font-bold text-gray-400 uppercase tracking-wider mb-1 md:mb-px">{children}</div>;
}

export function InputPanel({ inputs, onInputChange, operatingCurrency = 'CHF', readOnly = false }) {
  const set = (k) => (v) => {
    if (readOnly) return;
    onInputChange((prev) => ({ ...prev, [k]: clampInputValue(k, v) }));
  };
  const cur = (operatingCurrency || 'CHF').toUpperCase();
  const perSqm = `${cur}/m2`;

  return (
    <aside className="w-full md:w-[190px] bg-white border-b md:border-b-0 md:border-r border-gray-200 p-3 md:p-1.5 overflow-y-auto flex flex-col gap-2 md:gap-1 text-[12px] md:text-[9px]" data-testid="input-panel" data-readonly={readOnly ? 'true' : undefined}>
      {readOnly && (
        <div className="text-[8px] font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-1" data-testid="input-panel-readonly">
          Display only
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-1.5">
        {/* Land */}
        <section>
          <SectionTitle>Land</SectionTitle>
          <div className="space-y-1.5 md:space-y-px">
            <Field id="landPrice" label="Price" value={inputs.landPrice} onChange={set('landPrice')} unit={cur} step={50000} readOnly={readOnly} />
            <Field id="landSize" label="Size" value={inputs.landSize} onChange={set('landSize')} unit="m2" step={50} readOnly={readOnly} />
            <Field id="buildabilityCoefficient" label="CUS" value={inputs.buildabilityCoefficient} onChange={set('buildabilityCoefficient')} step={0.01} readOnly={readOnly} />
            <Field
              id="netSaleablePercent"
              label="Net %"
              value={Math.round(inputs.netSaleablePercent * 1000) / 10}
              onChange={(v) => set('netSaleablePercent')(v / 100)}
              unit="%"
              step={1}
              min={0}
              max={100}
              readOnly={readOnly}
            />
            <Field id="feasibilityStudy" label="Feasib." value={inputs.feasibilityStudy} onChange={set('feasibilityStudy')} unit={cur} step={1000} readOnly={readOnly} />
          </div>
        </section>

        {/* Product */}
        <section>
          <SectionTitle>Product</SectionTitle>
          <div className="space-y-1.5 md:space-y-px">
            <UnitsField
              value={inputs.numberOfUnits}
              onChange={(v) => {
                if (readOnly) return;
                onInputChange((prev) => applyUnitsChange(prev, v));
              }}
              readOnly={readOnly}
            />
            <Field id="pricePerSqm" label={perSqm} value={inputs.pricePerSqm} onChange={set('pricePerSqm')} unit={cur} step={500} readOnly={readOnly} />
            {(Number(inputs.apartmentPricePerSqm) > 0 || Number(inputs.housePricePerSqm) > 0) && (
              <div className="rounded border border-indigo-100 bg-indigo-50/40 px-1.5 py-1 space-y-1" data-testid="product-market-split">
                <div className="text-[7px] font-semibold uppercase tracking-wide text-indigo-600">Market split</div>
                {Number(inputs.apartmentPricePerSqm) > 0 && (
                  <Field
                    id="apartmentPricePerSqm"
                    label={`Apt ${perSqm}`}
                    value={inputs.apartmentPricePerSqm}
                    onChange={(v) => {
                      if (readOnly) return;
                      onInputChange((prev) => withSyncedSalePrice({ apartmentPricePerSqm: v }, prev));
                    }}
                    unit={cur}
                    step={50}
                    readOnly={readOnly}
                  />
                )}
                {Number(inputs.housePricePerSqm) > 0 && (
                  <Field
                    id="housePricePerSqm"
                    label={`House ${perSqm}`}
                    value={inputs.housePricePerSqm}
                    onChange={(v) => {
                      if (readOnly) return;
                      onInputChange((prev) => withSyncedSalePrice({ housePricePerSqm: v }, prev));
                    }}
                    unit={cur}
                    step={50}
                    readOnly={readOnly}
                  />
                )}
                {Number(inputs.apartmentPricePerSqm) > 0 && Number(inputs.housePricePerSqm) > 0 && (
                  <Field
                    id="apartmentSaleableShare"
                    label="Apt share %"
                    value={Math.round((inputs.apartmentSaleableShare ?? 0.5) * 100)}
                    onChange={(v) => {
                      if (readOnly) return;
                      onInputChange((prev) => withSyncedSalePrice(
                        { apartmentSaleableShare: Math.min(1, Math.max(0, v / 100)) },
                        prev,
                      ));
                    }}
                    unit="%"
                    step={5}
                    min={0}
                    max={100}
                    readOnly={readOnly}
                  />
                )}
              </div>
            )}
            <Field id="parkingPrice" label="Parking" value={inputs.parkingPrice} onChange={set('parkingPrice')} unit={cur} step={5000} readOnly={readOnly} />
            <Field
              id="numberOfParkingSpots"
              label="Spots"
              value={inputs.numberOfParkingSpots}
              onChange={(v) => {
                if (readOnly) return;
                onInputChange((prev) => applyParkingSpotsChange(prev, clampInputValue('numberOfParkingSpots', v)));
              }}
              step={1}
              readOnly={readOnly}
            />
          </div>
        </section>
      </div>

      {!readOnly && (
        <div className="text-[7px] text-gray-400 italic hidden md:block mt-1">Financing config in Settings (gear icon)</div>
      )}
    </aside>
  );
}
